import { FC, useRef, useState } from 'react';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { Spinner } from 'view/components';
import { palette } from 'view/themes/palette';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';

interface CreateCompanyLogoUploadProps {
  onUpload: (fileId: string, link: string) => void;
  onRemove: () => void;
  currentLink?: string;
  prefix?: string;
  postfix?: string;
}

export const CreateCompanyLogoUpload: FC<CreateCompanyLogoUploadProps> = ({
  onUpload,
  onRemove,
  currentLink,
  prefix = 'companies',
  postfix = 'logos',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { send, isLoading } = useRequest({
    url: '/api/v1/media/upload',
    method: ApiMethodsEnum.Post,
  });

  const [preview, setPreview] = useState<string | null>(currentLink || null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('files', file);
    formData.append('prefix', prefix);
    formData.append('postfix', postfix);

    const result = await send(formData);

    if (result && Array.isArray(result) && result.length > 0) {
      const uploadedFile = result[0];
      setPreview(uploadedFile.link);
      onUpload(uploadedFile.id, uploadedFile.link);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <label
        htmlFor="company-logo-upload"
        style={{
          display: 'block',
          fontSize: '14px',
          marginBottom: '8px',
          color: palette.text.body,
        }}
      >
        Company Logo
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div
          role="button"
          tabIndex={0}
          id="company-logo-upload"
          onClick={() => {
            if (!isLoading) {
              fileInputRef.current?.click();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              fileInputRef.current?.click();
            }
          }}
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '8px',
            border: `1px dashed ${palette.border.default}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: isLoading ? 'default' : 'pointer',
            background: palette.background.light,
            position: 'relative',
          }}
        >
          {isLoading ? (
            <Spinner variant="contained" />
          ) : preview ? (
            <img
              src={preview}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <UploadIcon style={{ color: palette.unknown.icon_grey }} />
          )}
        </div>
        {preview && !isLoading && (
          <IconButton onClick={handleRemove} size="small" color="error">
            <DeleteIcon />
          </IconButton>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="image/*"
        />
      </div>
    </div>
  );
};
